<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Loader from '~/components/ui/Loader.vue'
import { useAuthStore } from '~/stores/auth'
import { useBasketStore } from '~/stores/basket'

defineProps<{
    darkMode: boolean
    googleLoginLink: string
}>()

const email = ref('')
const password = ref('')

const loading = ref(false)

const emit = defineEmits(['onLogged'])

const uidCookie = useUidCookie()
const errorMessage = ref('')
const i18n = useI18n()

const code = ref('')
const verifyPhase = ref(false)
const attemptId = ref<string>('')

const login = async () => {
    loading.value = true
    try {
        const loginResponse = await useNuxtApp().$repository.login(email.value, password.value, uidCookie.value)
        if (!loginResponse.result) {
            errorMessage.value = loginResponse.message
            loading.value = false

            return
        }

        if (loginResponse.response.verificationMethod === 'none') {
            await useAuthStore().initFromLogin(loginResponse.response.data.token)
            uidCookie.value = loginResponse.response.data.uid
            await useBasketStore().fetchData(true, loginResponse.response.data.uid)
            loading.value = false
            emit('onLogged')
            useNuxtApp().$bus.$emit('cartUserLoggedIn')

            return
        }

        attemptId.value = loginResponse.response.data.attemptId
        verifyPhase.value = true
        loading.value = false
    } catch (e) {
        errorMessage.value = i18n.t('cart.step2.loginModal.incorrectLogin')
        loading.value = false
    }
}

const loginVerify = async () => {
    loading.value = true
    try {
        const response = await useNuxtApp().$repository.loginVerify(attemptId.value, code.value, uidCookie.value)
        if (!response.result) {
            errorMessage.value = response.message
            loading.value = false

            if (response.response?.cancelVerification) {
                verifyPhase.value = false
            }

            return
        }
        await useAuthStore().initFromLogin(response.response.token)
        uidCookie.value = response.response.uid

        await useBasketStore().fetchData(true, response.response.uid)

        loading.value = false

        emit('onLogged')

        useNuxtApp().$bus.$emit('cartUserLoggedIn')
    } catch (e) {
        errorMessage.value = i18n.t('cart.step2.loginModal.incorrectLogin')
        loading.value = false
    }
}
</script>

<template>
    <UiMessage v-if="errorMessage" :text="errorMessage" type="error" class="mb-3" />
    <template v-if="verifyPhase">
        <p><strong v-html="$t('loginPage.verifyDescription')"></strong></p>
        <form class="login__form login__form--verify" :class="{ 'login__form--dark': darkMode }" @submit.prevent="loginVerify">
            <fieldset class="form-group">
                <label class="login__label" for="code">{{ $t('cart.login.verificationCode') }}:</label>
                <input id="code" v-model="code" class="login__input" data-cy="login-code" type="text" required autofocus />
            </fieldset>
            <div class="login__actions">
                <button type="submit" class="login__button btn btn-primary btn-150 d-block">
                    {{ $t('login.login') }}
                </button>
            </div>
            <Loader v-if="loading" />
        </form>
    </template>

    <form v-else class="login__form" :class="{ 'login__form--dark': darkMode }" @submit.prevent="login">
        <fieldset class="form-group">
            <label class="login__label" for="email">{{ $t('cart.login.email') }}:</label>
            <input id="email" v-model="email" class="login__input" data-cy="login-email" type="email" required autofocus />
        </fieldset>
        <fieldset class="form-group">
            <label class="login__label" for="password">{{ $t('cart.login.password') }}:</label>
            <input id="password" v-model="password" type="password" required class="login__input" data-cy="login-password" />
        </fieldset>
        <div class="login__actions">
            <button type="submit" class="login__button btn btn-primary btn-150 d-block">
                {{ $t('login.login') }}
            </button>
        </div>
        <div class="login__actions">
            <a :href="googleLoginLink" class="login__google btn">
                <img src="/nuxt-assets/images/icons/login-google.svg" height="16" width="16" />
                {{ $t('login.viaGoogle') }}
            </a>
        </div>

        <Loader v-if="loading" />
    </form>
</template>

<style scoped lang="scss">
.login {
    &__form {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;

        &--dark {
            .login__input {
                background: $color-grey;
            }
        }

        @include screen-md {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
        }

        &--verify {
            grid-template-columns: 1fr;
        }
    }

    &__label {
        font-size: 13px;
        color: $color-dark;
        margin-bottom: 5px;
    }

    &__input {
        font-size: 12px;
        color: $color-dark;
        display: block;
        width: 100%;
        padding: 0.55rem 0.75rem;
        font-weight: 400;
        border: none;
        border-radius: $border-radius;
    }

    &__button {
        padding: 0.5rem;
        width: 100%;
    }

    &__google {
        padding: 0.5rem;
        width: 100%;
        color: $color-grey-extra-dark;
        background-color: $color-white;
        border-color: $color-grey-extra-dark;
        display: flex;
        justify-content: center;
        gap: 8px;
    }
}
</style>
